import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SupplierTierTable = ({ suppliers, onSupplierClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');

  const calculateTier = (totalOrders) => {
    const revenue = (totalOrders || 0) * 15;
    if (revenue >= 150000) return 'platinum';
    if (revenue >= 75000) return 'gold';
    if (revenue >= 25000) return 'silver';
    return 'bronze';
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze':
        return '#CD7F32';
      case 'silver':
        return '#C0C0C0';
      case 'gold':
        return '#FFD700';
      case 'platinum':
        return '#E5E4E2';
      default:
        return 'var(--color-primary)';
    }
  };

  const filteredSuppliers = suppliers?.filter((supplier) => {
    const matchesSearch = supplier?.business_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         supplier?.user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const tier = calculateTier(supplier?.total_orders);
    const matchesTier = filterTier === 'all' || tier === filterTier;
    return matchesSearch && matchesTier;
  });

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Icon name="Users" size={24} color="var(--color-primary)" />
          <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">Supplier Management</h3>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search suppliers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full sm:w-64 pl-10 pr-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            />
          </div>
          <select
            value={filterTier}
            onChange={(e) => setFilterTier(e?.target?.value)}
            className="px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          >
            <option value="all">All Tiers</option>
            <option value="bronze">Bronze</option>
            <option value="silver">Silver</option>
            <option value="gold">Gold</option>
            <option value="platinum">Platinum</option>
          </select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-caption font-semibold text-muted-foreground">Business</th>
              <th className="text-left py-3 px-4 text-sm font-caption font-semibold text-muted-foreground">Email</th>
              <th className="text-left py-3 px-4 text-sm font-caption font-semibold text-muted-foreground">Tier</th>
              <th className="text-left py-3 px-4 text-sm font-caption font-semibold text-muted-foreground">Revenue</th>
              <th className="text-left py-3 px-4 text-sm font-caption font-semibold text-muted-foreground">Orders</th>
              <th className="text-left py-3 px-4 text-sm font-caption font-semibold text-muted-foreground">Rating</th>
              <th className="text-left py-3 px-4 text-sm font-caption font-semibold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSuppliers?.map((supplier) => {
              const tier = calculateTier(supplier?.total_orders);
              const revenue = (supplier?.total_orders || 0) * 15;
              return (
                <tr key={supplier?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Store" size={18} color="var(--color-primary)" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground text-sm">{supplier?.business_name}</p>
                        <p className="text-xs text-muted-foreground">{supplier?.country}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{supplier?.user?.email}</td>
                  <td className="py-3 px-4">
                    <span 
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold capitalize"
                      style={{ 
                        backgroundColor: `${getTierColor(tier)}20`,
                        color: getTierColor(tier)
                      }}
                    >
                      {tier}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm font-semibold text-foreground data-text">
                    ${revenue?.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{supplier?.total_orders || 0}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={14} color="var(--color-warning)" />
                      <span className="text-sm font-semibold text-foreground">{supplier?.rating?.toFixed(1) || '0.0'}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onSupplierClick?.(supplier)}
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredSuppliers?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No suppliers found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierTierTable;